/**
 * Patched version of @radix-ui/react-compose-refs for React 19 compatibility.
 *
 * React 19 treats the return value of callback refs as cleanup functions AND
 * re-invokes ref callbacks when their identity changes. The original Radix
 * `composeRefs()` creates a new function every render call, causing:
 *   new identity → React detach(null)/attach(el) → setState(el) → re-render → loop
 *
 * Fixes applied:
 * 1. `setRef()` defers leaf function ref calls (state setters like setTrigger)
 *    to a microtask when called from within a composed ref chain. This moves
 *    state updates out of React's commit phase, preventing nestedUpdateCount
 *    from exceeding the limit of 50 when many components (tooltips etc.) mount
 *    simultaneously. Composed ref callbacks are still called synchronously so
 *    inner object refs (useRef.current) are set immediately.
 * 2. `composeRefs()` returns a **cached stable function** keyed on any stable ref
 *    object (from useRef) or stable function (React state setters).
 * 3. `useComposedRefs()` returns a stable callback via useCallback with node
 *    deduplication to break setState → re-render → ref(sameNode) loops.
 * 4. `setRef()` never returns cleanup functions (prevents cleanup propagation).
 * 5. Depth guard prevents infinite recursion from nested composed refs.
 *
 * See: https://github.com/radix-ui/primitives/issues/3799
 */
import * as React from 'react';

type PossibleRef<T> = React.Ref<T> | undefined;

/**
 * Module-level depth counter to prevent infinite ref composition recursion.
 * Normal Radix nesting (Slot inside Slot inside Dialog) is 2-3 levels deep.
 */
let _refDepth = 0;
const MAX_REF_DEPTH = 8;

/**
 * Set of composed ref callbacks. Used by setRef to distinguish composed refs
 * (which must run synchronously to process their inner refs) from leaf function
 * refs (state setters like setTrigger, which can be deferred to a microtask).
 */
const _composedRefs = new WeakSet<Function>();

/**
 * Tracks the last value passed to each function ref to prevent redundant calls.
 * When multiple composed refs nest (SlotClone → PopperAnchor → TooltipTrigger),
 * a single DOM element propagates through setRef at each level. This prevents
 * redundant state setter calls with the same value.
 * WeakRef is used so DOM elements can still be garbage-collected.
 */
const _fnRefLastValue = new WeakMap<Function, WeakRef<object>>();

function setRef<T>(ref: PossibleRef<T>, value: T) {
  if (typeof ref === 'function') {
    // Dedup: skip if this function ref was already called with this exact value.
    if (value !== null && value !== undefined && typeof value === 'object') {
      const lastWR = _fnRefLastValue.get(ref);
      if (lastWR) {
        const lastVal = lastWR.deref();
        if (lastVal === value) return; // Already called with this value — skip
      }
      _fnRefLastValue.set(ref, new WeakRef(value as object));
    } else if (value === null) {
      _fnRefLastValue.delete(ref);
    }

    // If we're inside a composed ref chain (_refDepth > 0) and this is a
    // LEAF function ref (NOT another composed ref callback), defer the call
    // to a microtask. This moves state setter calls (setTrigger, setAnchor etc.)
    // out of React's commit phase, so they don't increment nestedUpdateCount.
    // The microtask runs after the commit completes (root is idle), so
    // all deferred state updates start with a fresh nestedUpdateCount of 0.
    //
    // Composed ref callbacks are called synchronously because they need to
    // process their inner refs (including object refs like useRef.current)
    // before useLayoutEffect runs.
    if (_refDepth > 0 && !_composedRefs.has(ref)) {
      const fn = ref;
      const val = value;
      queueMicrotask(() => fn(val));
    } else {
      // Call but do NOT return the result — prevents cleanup chain propagation
      ref(value);
    }
  } else if (ref !== null && ref !== undefined) {
    (ref as React.MutableRefObject<T>).current = value;
  }
}

/**
 * Cache for `composeRefs()` — keyed on a stable ref found in the arguments.
 */
interface ComposeCacheEntry {
  fn: (node: any) => void;
  latestRefs: PossibleRef<any>[];
  lastNode: any;
}
const _composeCache = new WeakMap<object, ComposeCacheEntry>();

function composeRefs<T>(...refs: PossibleRef<T>[]) {
  // Find a stable identity to use as cache key.
  // Prefer object refs (useRef results, guaranteed stable) checking later
  // arguments first because the first argument (forwardedRef) is often the
  // unstable one created by a parent component.
  let cacheKey: object | undefined;
  for (let i = refs.length - 1; i >= 0; i--) {
    const r = refs[i];
    if (r != null && typeof r === 'object') {
      cacheKey = r;
      break;
    }
  }
  // Fallback: try function refs (React state setters are stable)
  if (!cacheKey) {
    for (let i = refs.length - 1; i >= 0; i--) {
      const r = refs[i];
      if (r != null && typeof r === 'function') {
        cacheKey = r;
        break;
      }
    }
  }

  if (cacheKey) {
    const cached = _composeCache.get(cacheKey);
    if (cached) {
      cached.latestRefs = refs as PossibleRef<any>[];
      return cached.fn;
    }

    const entry: ComposeCacheEntry = {
      fn: undefined as any,
      latestRefs: refs as PossibleRef<any>[],
      lastNode: undefined,
    };

    entry.fn = (node: any) => {
      if (node !== null && node === entry.lastNode) return;
      if (node !== null) entry.lastNode = node;

      if (_refDepth >= MAX_REF_DEPTH) return;
      _refDepth++;
      try {
        entry.latestRefs.forEach((ref) => setRef(ref, node));
      } finally {
        _refDepth--;
      }
    };

    // Mark as composed ref so setRef calls it synchronously
    _composedRefs.add(entry.fn);

    _composeCache.set(cacheKey, entry);
    return entry.fn;
  }

  // No cacheable ref found — fall back to a plain function.
  const fallback = (node: T) => {
    if (_refDepth >= MAX_REF_DEPTH) return;
    _refDepth++;
    try {
      refs.forEach((ref) => setRef(ref, node));
    } finally {
      _refDepth--;
    }
  };
  _composedRefs.add(fallback);
  return fallback;
}

function useComposedRefs<T>(...refs: PossibleRef<T>[]) {
  const refsRef = React.useRef(refs);
  refsRef.current = refs;

  const lastNodeRef = React.useRef<T | null>(null);

  const cb = React.useCallback((node: T | null) => {
    if (node !== null && node === lastNodeRef.current) return;
    if (node !== null) lastNodeRef.current = node;

    if (_refDepth >= MAX_REF_DEPTH) return;
    _refDepth++;
    try {
      const currentRefs = refsRef.current;
      currentRefs.forEach((ref) => setRef(ref, node as T));
    } finally {
      _refDepth--;
    }
  }, []);

  // Mark as composed ref so setRef calls it synchronously (useCallback
  // returns the same function object every render, so this is idempotent)
  _composedRefs.add(cb);

  return cb;
}

export { composeRefs, useComposedRefs };
